import { render } from "preact";
import { useLayoutEffect } from "preact/hooks";
import { LocationProvider, Router, Route, useLocation } from "preact-iso";
import {
  busy,
  stop,
  disconnect,
  message,
  storageNotice,
  takes,
  takesCount,
  hasMoreTakes,
  loadingTakes,
  loadTakes,
  currentTake,
  editing,
  transferring,
} from "./state";
import { Icon } from "./icons";
import { ScriptEditor } from "./editor";
import { Viewfinder } from "./studio";
import { TakeCard } from "./takes";
import { LlmPrompt } from "./llm-prompt";

// iOS checks touch-action on click-responsive targets. Include static content
// without cancelling taps or pinch zoom, as in gym-tracker.
// https://bugs.webkit.org/show_bug.cgi?id=149854#c25
document.body.addEventListener("click", function () {});

const standalone = matchMedia("(display-mode: standalone)");
function updateStandalone() {
  const installed =
    standalone.matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone;
  document.documentElement.classList.toggle("standalone", !!installed);
}
updateStandalone();
standalone.addEventListener("change", updateStandalone);

function Header() {
  const { path } = useLocation();
  return (
    <header
      class="topbar"
      onClick={function (event) {
        if (busy.value) event.preventDefault();
      }}
    >
      <a
        class="brand"
        href="/"
        aria-label="Cue home"
        aria-disabled={busy.value}
      >
        <span class="brand-mark">
          c<span>u</span>e
        </span>
      </a>
      <nav class="header-actions" aria-label="App navigation">
        <a
          class="quiet"
          href="/llm-prompt"
          aria-current={path === "/llm-prompt" ? "page" : undefined}
          aria-disabled={busy.value}
        >
          Get LLM prompt
        </a>
        <a
          class="quiet"
          href="/history"
          aria-current={path === "/history" ? "page" : undefined}
          aria-disabled={busy.value}
        >
          <Icon name="library" /> My takes{" "}
          <span class="count">{takesCount.value}</span>
        </a>
      </nav>
    </header>
  );
}

function Studio() {
  const { path } = useLocation();
  const camera = path === "/capture";
  return (
    <>
      <nav class="mobile-tabs" aria-label="Studio view">
        <a href="/" aria-current={!camera ? "page" : undefined}>
          Script
        </a>
        <a href="/capture" aria-current={camera ? "page" : undefined}>
          Camera
        </a>
      </nav>
      <div class="workspace">
        <ScriptEditor active={!camera} />
        <Viewfinder active={camera} />
      </div>
    </>
  );
}

function Library() {
  return (
    <section class="library">
      <div class="library-heading">
        <h2>
          My takes <span class="subtle">/ {takesCount.value}</span>
        </h2>
        <a class="quiet" href="/">
          <Icon name="back" /> Back to studio
        </a>
      </div>
      {loadingTakes.value && !takes.value.length ? (
        <p>Loading takes…</p>
      ) : takes.value.length ? (
        <div class="take-grid">
          {takes.value.map(function (take) {
            return <TakeCard key={take.id} take={take} />;
          })}
        </div>
      ) : (
        <div class="empty-library">
          <Icon name="camera" size={36} />
          <h3>No recordings</h3>
          <p>Your recordings will appear here, saved on this device.</p>
          <a class="primary" href="/capture">
            Record a take
          </a>
        </div>
      )}
      {hasMoreTakes.value && (
        <button
          class="outline-button"
          disabled={loadingTakes.value}
          onClick={loadTakes}
        >
          {loadingTakes.value ? "Loading…" : "Load more takes"}
        </button>
      )}
    </section>
  );
}

function NotFound() {
  return (
    <section class="empty-library">
      <h2>Page not found</h2>
      <a class="quiet" href="/">
        Back to script
      </a>
    </section>
  );
}

function App() {
  const { path } = useLocation();
  const inStudio = path === "/" || path === "/capture";
  useLayoutEffect(
    function () {
      if (!inStudio) {
        stop();
        disconnect();
      }
    },
    [inStudio],
  );
  return (
    <div class={`app-shell ${path === "/capture" ? "camera-mode" : ""}`}>
      <Header />
      <main>
        <Router>
          <Route path="/" component={Studio} />
          <Route path="/capture" component={Studio} />
          <Route path="/history" component={Library} />
          <Route path="/llm-prompt" component={LlmPrompt} />
          <Route default component={NotFound} />
        </Router>
        {(message.value || storageNotice.value) && (
          <div class="notice" role="status">
            <span>{message.value || storageNotice.value}</span>
            <button
              class="icon-button"
              aria-label="Dismiss message"
              onClick={function () {
                message.value = "";
                storageNotice.value = "";
              }}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
render(
  <LocationProvider>
    <App />
  </LocationProvider>,
  document.getElementById("app")!,
);

// Keep updates away from live capture and unsaved editing/review on every route.
async function setupUpdates() {
  const { setupServiceWorkerReloadWhenIdle } = await import("./sw-reload.js");
  await setupServiceWorkerReloadWhenIdle({
    isIdle: function () {
      return (
        !busy.value &&
        !editing.value &&
        !transferring.value &&
        !currentTake.value
      );
    },
  });
}
void setupUpdates();
